import React, { useEffect, useState } from "react";
import { AppDialog } from "components/common/dialogs";
import { Add, DeleteOutline, DescriptionOutlined, PictureAsPdfOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import SectionCard from "./SectionCard";
import { AppButton } from "components/common/buttons";
import { AppAutocomplete, AppTextField } from "components/common/inputs";
import { formatInputDate } from "utils/formatters";

const getOptionValue = (option) => option?.value ?? option?.Value;
const getOptionLabel = (option) => option?.label ?? option?.Label ?? "";
const linesFromValue = (value) => Array.isArray(value) ? value : [];

export default function AssignmentDecisionDialog({
  open,
  form,
  teamOptions = [],
  error,
  loading = false,
  downloading = false,
  downloadFormat = null,
  onClose,
  onDownloadPdf,
  onDownloadWord,
}) {
  const [localForm, setLocalForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLocalForm(form ? { ...form } : null);
  }, [form, open]);

  const updateForm = (name, value) => {
    setLocalForm((current) => ({ ...current, [name]: value }));
  };

  const updateLine = (name, index, value) => {
    setLocalForm((current) => ({
      ...current,
      [name]: linesFromValue(current?.[name]).map((line, lineIndex) => lineIndex === index ? value : line),
    }));
  };

  const addLine = (name, value = "") => {
    setLocalForm((current) => ({
      ...current,
      [name]: [...linesFromValue(current?.[name]), value],
    }));
  };

  const removeLine = (name, index) => {
    setLocalForm((current) => ({
      ...current,
      [name]: linesFromValue(current?.[name]).filter((_, lineIndex) => lineIndex !== index),
    }));
  };

  const updatePeople = (name, textName, selectedOptions, includeTitle) => {
    const ids = selectedOptions.map(getOptionValue).filter((value) => value != null);
    const text = selectedOptions
      .map((option) => getOptionLabel(option))
      .map((label) => includeTitle ? label : label.replace(/^.*?-\s*/, ""))
      .join("; ");

    setLocalForm((current) => ({
      ...current,
      [name]: ids,
      [textName]: text,
    }));
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      className="subcontract-dialog"
      PaperProps={{ sx: { height: "min(92vh, 980px)", display: "flex" } }}
      title="Tạo quyết định giao khoán"
      loading={loading || downloading}
      actions={
        <>
          <AppButton variant="outlined" color="inherit" onClick={onClose} disabled={loading || downloading}>Hủy</AppButton>
          <AppButton variant="contained" startIcon={<DescriptionOutlined />} onClick={() => onDownloadWord?.(localForm)} disabled={loading || downloading || !localForm}>
            {downloadFormat === "word" ? "Đang tạo Word..." : "Tải Word"}
          </AppButton>
          <AppButton variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={() => onDownloadPdf?.(localForm)} disabled={loading || downloading || !localForm}>
            {downloadFormat === "pdf" ? "Đang tạo PDF..." : "Tải PDF"}
          </AppButton>
        </>
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      {loading || !localForm ? (
        <Box sx={{ minHeight: 260, display: "grid", placeItems: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box className="subcontract-form-grid">
            <AppTextField disabled={loading || downloading} label="Số văn bản" required value={localForm.documentNumber || ""} onChange={(value) => updateForm("documentNumber", value)} />
            <AppTextField disabled={loading || downloading} label="Ký hiệu" value={localForm.documentSymbol || "QĐ-TĐ"} onChange={(value) => updateForm("documentSymbol", value)} />
            <AppTextField disabled={loading || downloading} label="Địa danh" required value={localForm.place || ""} onChange={(value) => updateForm("place", value)} />
            <AppTextField disabled={loading || downloading} label="Ngày ban hành" type="date" value={formatInputDate(localForm.issueDate)} onChange={(value) => updateForm("issueDate", value)} />
            <AppTextField disabled={loading || downloading} label="Trích yếu nội dung" className="full" multiline minRows={2} value={localForm.summary || ""} onChange={(value) => updateForm("summary", value)} />
            <AppTextField disabled={loading || downloading} label="Chủ thể ban hành" className="full" value={localForm.authority || ""} onChange={(value) => updateForm("authority", value)} />
          </Box>

          <SectionCard title="Căn cứ">
            <Box className="subcontract-stack">
              {linesFromValue(localForm.legalBasis).map((line, index) => (
                <Box key={`legal-${index}`} sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 1 }}>
                  <AppTextField disabled={loading || downloading} label={`Đoạn ${index + 1}`} multiline minRows={2} value={line} onChange={(value) => updateLine("legalBasis", index, value)} />
                  <Tooltip title="Xóa đoạn">
                    <IconButton disabled={loading || downloading} color="error" onClick={() => removeLine("legalBasis", index)} sx={{ alignSelf: "start" }}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              <Box>
                <AppButton disabled={loading || downloading} size="small" startIcon={<Add />} onClick={() => addLine("legalBasis")}>Thêm căn cứ</AppButton>
              </Box>
            </Box>
          </SectionCard>

          <SectionCard title="Nội dung quyết định">
            <Box className="subcontract-form-grid">
              <AppAutocomplete
                multiple
                disabled={loading || downloading}
                label="Chỉ đạo sản xuất"
                options={teamOptions.map((option) => ({ ...option, value: getOptionValue(option), label: getOptionLabel(option) }))}
                value={localForm.productionDirectorIds || []}
                onChange={(_, options) => updatePeople("productionDirectorIds", "productionDirectorText", options, true)}
              />
              <AppAutocomplete
                multiple
                disabled={loading || downloading}
                label="Phòng KHTH"
                options={teamOptions.map((option) => ({ ...option, value: getOptionValue(option), label: getOptionLabel(option) }))}
                value={localForm.planningDepartmentIds || []}
                onChange={(_, options) => updatePeople("planningDepartmentIds", "planningDepartmentText", options, false)}
              />
              <AppTextField disabled={loading || downloading} label="Chỉ đạo sản xuất - nội dung" className="full" value={localForm.productionDirectorText || ""} onChange={(value) => updateForm("productionDirectorText", value)} />
              <AppTextField disabled={loading || downloading} label="Phòng KHTH - nội dung" className="full" value={localForm.planningDepartmentText || ""} onChange={(value) => updateForm("planningDepartmentText", value)} />
              <AppTextField disabled={loading || downloading} label="Bộ phận sản xuất" className="full" value={localForm.productionUnitText || ""} onChange={(value) => updateForm("productionUnitText", value)} />
              <AppTextField disabled={loading || downloading} label="Nội dung, khối lượng công việc" className="full" multiline minRows={2} value={localForm.workContent || ""} onChange={(value) => updateForm("workContent", value)} />
              <AppTextField disabled={loading || downloading} label="Yêu cầu kỹ thuật" className="full" multiline minRows={2} value={localForm.technicalRequirements || ""} onChange={(value) => updateForm("technicalRequirements", value)} />
              <AppTextField disabled={loading || downloading} label="Kinh phí bảo đảm" className="full" multiline minRows={2} value={localForm.fundingGuarantee || ""} onChange={(value) => updateForm("fundingGuarantee", value)} />
              <AppTextField disabled={loading || downloading} label="Điều 2" className="full" multiline minRows={3} value={localForm.article2 || ""} onChange={(value) => updateForm("article2", value)} />
            </Box>
          </SectionCard>

          <SectionCard title="Nơi nhận và chữ ký">
            <Box className="subcontract-stack">
              {linesFromValue(localForm.recipients).map((line, index) => (
                <Box key={`recipient-${index}`} sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 1 }}>
                  <AppTextField disabled={loading || downloading} label={`Nơi nhận ${index + 1}`} value={line} onChange={(value) => updateLine("recipients", index, value)} />
                  <Tooltip title="Xóa nơi nhận">
                    <IconButton disabled={loading || downloading} color="error" onClick={() => removeLine("recipients", index)} sx={{ alignSelf: "start" }}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              <Box>
                <AppButton disabled={loading || downloading} size="small" startIcon={<Add />} onClick={() => addLine("recipients")}>Thêm nơi nhận</AppButton>
              </Box>
              <Box className="subcontract-form-grid">
                <AppTextField disabled={loading || downloading} label="Chức vụ ký" value={localForm.signatureTitle || "GIÁM ĐỐC"} onChange={(value) => updateForm("signatureTitle", value)} />
                <AppTextField disabled={loading || downloading} label="Tên giám đốc" value={localForm.directorName || ""} onChange={(value) => updateForm("directorName", value)} />
              </Box>
            </Box>
          </SectionCard>
        </>
      )}
    </AppDialog>
  );
}
